import { useState } from 'react';

import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { useCurrentApproverDecision } from '../../../hooks/use-current-approver-decision';
import { useReviewTimeOffRequest } from '../../../hooks/use-review-time-off-request';
import { ApprovalStatus, TimeOffRequest } from '../../../types/time-off-request';
import { TimeOffReviewDecision } from '../../../types/time-off-review';

interface ReviewTimeOffModalProps {
  request: TimeOffRequest;
  decision: TimeOffReviewDecision;
  open: boolean;
  onClose: () => void;
  /** Fired only after a decision is successfully submitted (not on cancel). */
  onReviewed?: () => void;
}

const COMMENT_FIELD_ID = 'time-off-review-comment';

/**
 * Approve/decline confirmation modal for a single time-off request. Copy and the
 * confirm-button variant are driven by `decision`. Both decisions accept an
 * optional comment. Built on the shared {@link ConfirmationModal}.
 */
export const ReviewTimeOffModal = ({
  request,
  decision,
  open,
  onClose,
  onReviewed,
}: ReviewTimeOffModalProps) => {
  const { t } = useTranslation('authenticated.timeOff');
  const approverDecision = useCurrentApproverDecision(request);
  const [comment, setComment] = useState('');

  const reviewMutation = useReviewTimeOffRequest({
    onSuccess: () => {
      setComment('');
      onClose();
      onReviewed?.();
    },
  });

  const isApprove = decision === TimeOffReviewDecision.Approve;
  const ns = isApprove ? 'approvals.approveModal' : 'approvals.declineModal';
  const employeeName = request.requestedBy.name;

  // This approver already decided and is now reversing their own decision.
  const isChangingDecision =
    approverDecision === ApprovalStatus.Approved || approverDecision === ApprovalStatus.Rejected;

  const handleClose = () => {
    setComment('');
    onClose();
  };

  return (
    <ConfirmationModal
      open={open}
      onOpen={() => undefined}
      onClose={handleClose}
      title={t(`${ns}.title`)}
      message={t(`${ns}.description`, { name: employeeName })}
      confirmText={t(`${ns}.confirm`)}
      cancelText={t('common:cancel')}
      confirmVariant={isApprove ? 'default' : 'destructive'}
      isLoading={reviewMutation.isPending}
      onConfirm={async () => {
        await reviewMutation.mutateAsync({ requestId: request.id, decision, comment });
      }}
    >
      {isChangingDecision && (
        <Alert variant="warning" className="mb-4">
          <AlertTriangle className="h-5 w-5" strokeWidth={1.8} />
          <AlertDescription className="font-medium leading-[1.55]">
            {t(
              approverDecision === ApprovalStatus.Approved
                ? 'approvals.changeDecisionWarning.fromApproved'
                : 'approvals.changeDecisionWarning.fromDeclined'
            )}
          </AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={COMMENT_FIELD_ID} className="text-sm font-semibold">
          {t(`${ns}.commentLabel`)}{' '}
          <span className="font-normal text-muted-foreground">{t(`${ns}.commentOptional`)}</span>
        </Label>
        <Textarea
          id={COMMENT_FIELD_ID}
          value={comment}
          onChange={event => setComment(event.target.value)}
          placeholder={t(`${ns}.commentPlaceholder`)}
          disabled={reviewMutation.isPending}
          className="min-h-[96px] resize-none"
        />
      </div>
    </ConfirmationModal>
  );
};
